import { cloneTableBlock, createId } from '@/lib/table-utils';
import { TestBlock, TextBlock } from '@/types/report-payload';
import { stripSplitBlockId } from '../pagination/utils';

export const BLOCK_CLIPBOARD_MIME = 'application/x-laudopsi-test-blocks';
export const BLOCK_CLIPBOARD_HTML_ATTR = 'data-laudopsi-test-blocks';

export interface BlockClipboardPayload {
    version: 1;
    mode: 'copy' | 'cut';
    reportId?: string;
    blocks: TestBlock[];
}

export type InsertTarget = 'start' | 'end' | { afterRealId: string };

/**
 * Stable key for a block. Legacy blocks may lack an id, in which case the
 * `order-N` fallback mirrors how handleDeleteBlock resolves them.
 */
export const getBlockRealId = (block: TestBlock): string =>
    block.id ? stripSplitBlockId(block.id) : `order-${block.order}`;

export const findBlockIndexByRealId = (blocks: TestBlock[], realId: string): number =>
    blocks.findIndex((block) => getBlockRealId(block) === realId);

/**
 * Reads a `data-preview-block-id` attribute back into a real id. The attribute
 * carries `block.id || block.order`, so a bare number means the block had no id
 * and resolves through the same `order-N` fallback as `getBlockRealId`.
 */
export const attrToRealId = (attr: string): string =>
    /^\d+$/.test(attr) ? `order-${attr}` : stripSplitBlockId(attr);

const isLinkedCompanionOf = (block: TestBlock, tableId: string): boolean =>
    (block.block_type === 'text' || block.block_type === 'chart' || block.block_type === 'image') &&
    !!block.linked_table_id &&
    stripSplitBlockId(block.linked_table_id) === stripSplitBlockId(tableId);

const cloneBlock = (block: TestBlock): TestBlock => {
    if (block.block_type === 'table') return cloneTableBlock(block);
    return structuredClone(block);
};

export const reindexBlocks = (blocks: TestBlock[]): TestBlock[] =>
    blocks.map((block, index) => ({
        ...block,
        id: block.id || createId('block'),
        order: index,
    }));

/**
 * Resolves the selection into the blocks that travel on the clipboard:
 * exactly the selected blocks, plus — when a selected block is a table —
 * its linked text/chart companions (matching the delete-cascade behavior).
 * Output is deep-cloned, in document order, with ids backfilled.
 */
export function collectBlocksForClipboard(
    blocks: TestBlock[],
    selectedRealIds: string[],
): TestBlock[] {
    const selected = new Set(selectedRealIds);
    const selectedTableIds = blocks
        .filter((block) => block.block_type === 'table' && selected.has(getBlockRealId(block)))
        .map((block) => block.id)
        .filter((id): id is string => !!id);

    return blocks
        .filter(
            (block) =>
                selected.has(getBlockRealId(block)) ||
                selectedTableIds.some((tableId) => isLinkedCompanionOf(block, tableId)),
        )
        .map((block) => {
            const clone = cloneBlock(block);
            return { ...clone, id: clone.id || createId('block') } as TestBlock;
        });
}

/**
 * Removes the given blocks with delete-cascade parity:
 * - removing a table also removes its linked text/chart companions;
 * - a linked text selected WITHOUT its table is cleared, not removed
 *   (same as handleDeleteBlock in TestSectionRenderer).
 */
export function removeBlocksWithCascade(blocks: TestBlock[], realIds: string[]): TestBlock[] {
    const targets = new Set(realIds);
    const removedTableIds = blocks
        .filter((block) => block.block_type === 'table' && targets.has(getBlockRealId(block)))
        .map((block) => block.id)
        .filter((id): id is string => !!id);

    const shouldRemove = (block: TestBlock): boolean => {
        if (removedTableIds.some((tableId) => isLinkedCompanionOf(block, tableId))) return true;
        if (!targets.has(getBlockRealId(block))) return false;

        // Linked text whose table survives is cleared instead of removed
        if (block.block_type === 'text' && block.linked_table_id) {
            const tableRemoved = removedTableIds.some(
                (tableId) =>
                    stripSplitBlockId(tableId) === stripSplitBlockId(block.linked_table_id),
            );
            return tableRemoved;
        }

        return true;
    };

    const next = blocks
        .filter((block) => !shouldRemove(block))
        .map((block) => {
            const isClearedLinkedText =
                block.block_type === 'text' &&
                !!block.linked_table_id &&
                targets.has(getBlockRealId(block));

            if (!isClearedLinkedText) return block;

            return {
                ...block,
                content: '',
                automatic: false,
                has_content: true,
            } as TextBlock;
        });

    return reindexBlocks(next);
}

/**
 * Inserts clipboard blocks into the target array.
 *
 * Id policy — remint on collision: incoming blocks whose id already exists in
 * the target get a fresh id (copy semantics); a cut payload pasted once keeps
 * its ids (a true move, links intact). `linked_table_id` is remapped for
 * companions whose table travelled in the same payload; a linked TEXT whose
 * table did not travel drops the link (it becomes a plain text block), while a
 * chart keeps it (multiple charts per table are valid and charts fall back to
 * cached datapoints).
 *
 * Insertion lands after the target block's contiguous linked companions so a
 * paste never wedges between a table and its interpretation text.
 */
export function insertClipboardBlocks(
    blocks: TestBlock[],
    payload: BlockClipboardPayload,
    target: InsertTarget,
): { blocks: TestBlock[]; insertedIds: string[] } {
    const existingIds = new Set(blocks.map((block) => block.id).filter((id): id is string => !!id));
    const payloadTableIds = new Set(
        payload.blocks
            .filter((block) => block.block_type === 'table')
            .map((block) => block.id)
            .filter((id): id is string => !!id),
    );

    const idMap = new Map<string, string>();
    const incoming = payload.blocks.map((block) => {
        const clone = cloneBlock(block);
        let id = clone.id;
        if (!id || existingIds.has(id)) {
            const nextId = createId('block');
            if (id) idMap.set(id, nextId);
            id = nextId;
        }
        existingIds.add(id);
        return { ...clone, id } as TestBlock;
    });

    const remapped = incoming.map((block) => {
        if (
            block.block_type !== 'text' &&
            block.block_type !== 'chart' &&
            block.block_type !== 'image'
        ) {
            return block;
        }
        const linkedId = block.linked_table_id;
        if (!linkedId) return block;

        if (payloadTableIds.has(linkedId)) {
            const mapped = idMap.get(linkedId);
            return mapped ? ({ ...block, linked_table_id: mapped } as TestBlock) : block;
        }

        // Table did not travel with the payload. A chart keeps the link (it
        // falls back to cached datapoints); text and image become standalone.
        if (block.block_type === 'text' || block.block_type === 'image') {
            const rest = { ...block };
            delete rest.linked_table_id;
            return rest as TestBlock;
        }

        return block;
    });

    const insertionIndex = resolveInsertionIndex(blocks, target);
    const next = [...blocks];
    next.splice(insertionIndex, 0, ...remapped);

    return {
        blocks: reindexBlocks(next),
        insertedIds: remapped.map((block) => block.id as string),
    };
}

/**
 * The blocks a move actually carries: the named ones, plus — when a named
 * block is a table — its linked text/chart/image companions.
 */
function collectMovingBlocks(blocks: TestBlock[], realIds: string[]): TestBlock[] {
    // Drag sources hand over fragment ids (`x__split__p2`); normalize here so
    // every caller resolves to the underlying block.
    const selected = new Set(realIds.map(stripSplitBlockId));
    const movedTableIds = blocks
        .filter((block) => block.block_type === 'table' && selected.has(getBlockRealId(block)))
        .map((block) => block.id)
        .filter((id): id is string => !!id);

    return blocks.filter(
        (block) =>
            selected.has(getBlockRealId(block)) ||
            movedTableIds.some((tableId) => isLinkedCompanionOf(block, tableId)),
    );
}

/**
 * Moves blocks to a new position, preserving their identity (no clone, no id
 * remint — comments and measurements stay keyed to the same blocks).
 *
 * Group semantics mirror the cut-cascade: moving a table carries its linked
 * text/chart companions. Moving a linked text alone just relocates it and
 * keeps the link — unlike removeBlocksWithCascade, nothing is cleared.
 *
 * No-op when the drop anchor sits inside the moving group or the order does
 * not change, so callers can apply the result unconditionally without
 * polluting undo history.
 */
export function moveBlocksToIndex(
    blocks: TestBlock[],
    realIds: string[],
    target: InsertTarget,
): TestBlock[] {
    const moving = collectMovingBlocks(blocks, realIds);
    if (moving.length === 0) return blocks;

    if (
        typeof target === 'object' &&
        moving.some((block) => getBlockRealId(block) === target.afterRealId)
    ) {
        return blocks;
    }

    const movingSet = new Set(moving);
    const remaining = blocks.filter((block) => !movingSet.has(block));
    const insertionIndex = resolveInsertionIndex(remaining, target);
    const next = [...remaining];
    next.splice(insertionIndex, 0, ...moving);

    const unchanged =
        next.length === blocks.length && next.every((block, i) => block === blocks[i]);
    if (unchanged) return blocks;

    return reindexBlocks(next);
}

export function resolveInsertionIndex(blocks: TestBlock[], target: InsertTarget): number {
    if (target === 'start') return 0;
    if (target === 'end') return blocks.length;

    const targetIndex = findBlockIndexByRealId(blocks, target.afterRealId);
    if (targetIndex === -1) return blocks.length;

    const targetBlock = blocks[targetIndex];
    let index = targetIndex + 1;

    // Skip past the table's contiguous companions
    if (targetBlock.block_type === 'table' && targetBlock.id) {
        while (index < blocks.length && isLinkedCompanionOf(blocks[index], targetBlock.id)) {
            index += 1;
        }
    }

    return index;
}

// --- Serialization -------------------------------------------------------

const encodeUnicodeBase64 = (value: string): string => btoa(encodeURIComponent(value));
const decodeUnicodeBase64 = (value: string): string => decodeURIComponent(atob(value));

/**
 * Serializes the payload for the system clipboard. The `text` variant doubles
 * as a fingerprint: on paste, matching text/plain confirms the clipboard was
 * not overwritten externally, so the internal payload can be trusted.
 */
export function serializeBlockClipboard(payload: BlockClipboardPayload): {
    json: string;
    html: string;
    text: string;
} {
    const json = JSON.stringify(payload);
    const html = `<div ${BLOCK_CLIPBOARD_HTML_ATTR}="${encodeUnicodeBase64(json)}"></div>`;
    const nonce = createId('clip');
    const text = `[LaudoPsi] ${payload.blocks.length} bloco(s) de laudo (${nonce})`;
    return { json, html, text };
}

const BLOCK_TYPES = new Set(['table', 'chart', 'text', 'divider', 'image']);

export function parseBlockClipboardJson(raw: string): BlockClipboardPayload | null {
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as BlockClipboardPayload;
        if (!parsed || parsed.version !== 1) return null;
        if (parsed.mode !== 'copy' && parsed.mode !== 'cut') return null;
        if (!Array.isArray(parsed.blocks) || parsed.blocks.length === 0) return null;
        const valid = parsed.blocks.every(
            (block) => block && typeof block === 'object' && BLOCK_TYPES.has(block.block_type),
        );
        return valid ? parsed : null;
    } catch {
        return null;
    }
}

export function extractPayloadFromHtml(html: string): BlockClipboardPayload | null {
    if (!html) return null;

    const match = html.match(new RegExp(`${BLOCK_CLIPBOARD_HTML_ATTR}="([^"]+)"`));
    if (!match) return null;

    try {
        return parseBlockClipboardJson(decodeUnicodeBase64(match[1]));
    } catch {
        return null;
    }
}
